<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useEquipmentsStore } from '@/store/equipments';

// Slugify function with input validation
const slugify = (text) => {
  if (typeof text !== 'string') return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');
};

const store = useEquipmentsStore();

const emit = defineEmits(['filter-applied']);

const selectedCategories = ref({});
const selectedCities = ref({});

// Category state
const categorySearch = ref('');
const categorySearchResults = ref(null); // null = not searching, array = search results
const loadingMoreCategories = ref(false);
const loadingCategorySearch = ref(false);

// City state
const loadingMoreCities = ref(false);

// Debounce timer
let searchTimer = null;

// Computed: which categories to display
const displayedCategories = computed(() => {
  if (categorySearchResults.value !== null) return categorySearchResults.value;
  return store.categories;
});

const categoriesHasMore = computed(() => {
  if (categorySearchResults.value !== null) return false; // search results are complete
  return store.categoriesHasMore;
});

const categoriesRemaining = computed(() => {
  return Math.max(0, store.categoriesTotal - store.categories.length);
});

const citiesHasMore = computed(() => store.citiesHasMore);
const citiesRemaining = computed(() => Math.max(0, store.citiesTotal - store.cities.length));

const categoriesExpanded = computed(() => store.categories.length > store.BATCH_SIZE);
const citiesExpanded = computed(() => store.cities.length > store.BATCH_SIZE);

// Computed properties for filter values
const getSelectedCategories = computed(() => {
  return Object.entries(selectedCategories.value)
    .filter(([_, isSelected]) => isSelected)
    .map(([name]) => slugify(name));
});

const getSelectedCities = computed(() => {
  return Object.entries(selectedCities.value)
    .filter(([_, isSelected]) => isSelected)
    .map(([name]) => name);
});

// Methods
const handleCheckboxChange = (event, value, type) => {
  if (type === 'category') {
    selectedCategories.value = {
      ...selectedCategories.value,
      [value]: event.target.checked
    };
  } else if (type === 'location') {
    selectedCities.value = {
      ...selectedCities.value,
      [value]: event.target.checked
    };
  }
};

const handleFilterChange = () => {
  const filters = {
    categories: getSelectedCategories.value,
    cities: getSelectedCities.value
  };
  store.fetchFilteredEquipments(filters);
  emit('filter-applied', filters);
};

const loadMoreCategories = async () => {
  loadingMoreCategories.value = true;
  try {
    await store.fetchCategories(true);
  } finally {
    loadingMoreCategories.value = false;
  }
};

const loadMoreCities = async () => {
  loadingMoreCities.value = true;
  try {
    await store.fetchCities(true);
  } finally {
    loadingMoreCities.value = false;
  }
};

const onCategorySearch = () => {
  clearTimeout(searchTimer);
  const query = categorySearch.value.trim();

  if (!query) {
    categorySearchResults.value = null;
    return;
  }

  loadingCategorySearch.value = true;
  searchTimer = setTimeout(async () => {
    try {
      const data = await store.searchCategories(query);
      categorySearchResults.value = data.results;
    } catch {
      categorySearchResults.value = [];
    } finally {
      loadingCategorySearch.value = false;
    }
  }, 300);
};

const clearCategorySearch = () => {
  categorySearch.value = '';
  categorySearchResults.value = null;
  loadingCategorySearch.value = false;
};

const clearAll = (filterType) => {
  if (filterType === 'category') {
    selectedCategories.value = {};
  } else if (filterType === 'location') {
    selectedCities.value = {};
  }
  handleFilterChange();
};

// Fetch initial batches on mount
onMounted(async () => {
  if (store.categories.length === 0) await store.fetchCategories();
  if (store.cities.length === 0) await store.fetchCities();
});

watch([selectedCategories, selectedCities], handleFilterChange, { deep: true });
</script>
<template>
  <div class="hidden md:block bg-theme-card rounded-xl shadow-md p-4 border border-theme-border">
    <!-- Title -->
    <h1 class="text-lg font-bold mb-4 flex items-center text-theme-text">
      <i class="pi pi-filter text-secondary-500 mr-2"></i> Filters
    </h1>

    <ul class="space-y-6">
      <!-- Category Filter -->
      <li class="product-filters-tab">
        <h2 class="text-base font-semibold text-theme-text mb-2">Category</h2>

        <!-- Search Input -->
        <div class="relative mb-2">
          <i class="pi pi-search absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-muted text-xs"></i>
          <input
            v-model="categorySearch"
            @input="onCategorySearch"
            type="text"
            placeholder="Search categories..."
            class="w-full pl-8 pr-8 py-2 text-sm bg-theme-bg2 border border-theme-border rounded-lg text-theme-text placeholder-theme-muted focus:outline-none focus:ring-1 focus:ring-secondary-500/40 focus:border-secondary-500 transition"
          />
          <button
            v-if="categorySearch"
            @click="clearCategorySearch"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-theme-muted hover:text-theme-text transition"
          >
            <i class="pi pi-times text-xs"></i>
          </button>
        </div>

        <div v-if="loadingCategorySearch" class="flex items-center gap-1.5 py-2 ml-1">
          <i class="pi pi-spin pi-spinner text-secondary-500 text-xs"></i>
          <span class="text-xs text-theme-muted">Searching...</span>
        </div>

        <!-- Category skeleton -->
        <div v-else-if="store.isLoadingCategories && displayedCategories.length === 0" class="ml-1 space-y-3">
          <div v-for="i in 6" :key="`cat-skel-${i}`" class="flex items-center gap-2">
            <div class="w-4 h-4 rounded bg-theme-bg2 relative overflow-hidden shrink-0"><div class="skeleton-shimmer"></div></div>
            <div class="h-3.5 bg-theme-bg2 rounded-full relative overflow-hidden" :style="{ width: `${50 + Math.random() * 40}%` }"><div class="skeleton-shimmer"></div></div>
          </div>
        </div>

        <ul
          v-else-if="displayedCategories.length"
          id="category-filter-list"
          class="ml-1 space-y-2 overflow-y-auto max-h-80 pr-1 custom-scrollbar"
        >
          <!-- Clear All -->
          <li>
            <button
              class="text-xs text-theme-muted hover:text-theme-text underline focus:outline-none"
              @click.prevent="clearAll('category')"
            >
              Clear All
            </button>
          </li>
          <!-- Categories -->
          <li v-for="category in displayedCategories" :key="category.id || category.name" class="flex items-center">
            <input
              :id="`category-checkbox-${slugify(category.name)}`"
              type="checkbox"
              class="form-checkbox h-4 w-4 text-secondary-500 focus:ring-secondary-500 rounded cursor-pointer"
              :checked="selectedCategories[category.name]"
              @change="handleCheckboxChange($event, category.name, 'category')"
            />
            <label
              :for="`category-checkbox-${slugify(category.name)}`"
              class="ml-2 text-sm text-theme-text2 hover:text-theme-text cursor-pointer transition"
            >
              {{ category.name }}
            </label>
          </li>
        </ul>
        <p v-else class="text-sm text-theme-muted ml-1">
          {{ categorySearch ? 'No categories match your search.' : 'No categories available.' }}
        </p>

        <!-- Load More Categories -->
        <button
          v-if="categoriesHasMore"
          @click="loadMoreCategories"
          :disabled="loadingMoreCategories"
          class="mt-2 text-xs font-medium text-secondary-500 hover:text-theme-text focus:outline-none disabled:opacity-50 flex items-center gap-1"
        >
          <i v-if="loadingMoreCategories" class="pi pi-spin pi-spinner text-[10px]"></i>
          <i v-else class="pi pi-chevron-down text-[10px]"></i>
          {{ loadingMoreCategories ? 'Loading...' : `Show More (${categoriesRemaining})` }}
        </button>

        <!-- Show Less Categories -->
        <button
          v-if="categoriesExpanded && !categoriesHasMore"
          @click="store.collapseCategories()"
          class="mt-2 text-xs font-medium text-secondary-500 hover:text-theme-text focus:outline-none flex items-center gap-1"
        >
          <i class="pi pi-chevron-up text-[10px]"></i>
          Show Less
        </button>
      </li>

      <hr class="border-theme-border" />

      <!-- Location Filter -->
      <li class="product-filters-tab">
        <h2 class="text-base font-semibold text-theme-text mb-2">Location</h2>
        
        <!-- City skeleton -->
        <div v-if="store.isLoadingCities && store.cities.length === 0" class="ml-1 space-y-3">
          <div v-for="i in 5" :key="`city-skel-${i}`" class="flex items-center gap-2">
            <div class="w-4 h-4 rounded bg-theme-bg2 relative overflow-hidden shrink-0"><div class="skeleton-shimmer"></div></div>
            <div class="h-3.5 bg-theme-bg2 rounded-full relative overflow-hidden" :style="{ width: `${40 + Math.random() * 45}%` }"><div class="skeleton-shimmer"></div></div>
          </div>
        </div>

        <ul
          v-else-if="store.cities.length"
          id="location-filter-list"
          class="ml-1 space-y-2 overflow-y-auto max-h-80 pr-1 custom-scrollbar"
        >
          <!-- Clear All -->
          <li>
            <button
              class="text-xs text-theme-muted hover:text-theme-text underline focus:outline-none"
              @click.prevent="clearAll('location')"
            >
              Clear All
            </button>
          </li>
          <!-- Cities -->
          <li v-for="city in store.cities" :key="city" class="flex items-center">
            <input
              :id="`city-checkbox-${slugify(city)}`"
              type="checkbox"
              class="form-checkbox h-4 w-4 text-secondary-500 focus:ring-secondary-500 rounded cursor-pointer"
              :checked="selectedCities[city]"
              @change="handleCheckboxChange($event, city, 'location')"
            />
            <label
              :for="`city-checkbox-${slugify(city)}`"
              class="ml-2 text-sm text-theme-text2 hover:text-theme-text cursor-pointer transition"
            >
              {{ city }}
            </label>
          </li>
        </ul>
        <p v-else class="text-sm text-theme-muted ml-1">No cities available.</p>

        <!-- Load More Cities -->
        <button
          v-if="citiesHasMore"
          @click="loadMoreCities"
          :disabled="loadingMoreCities"
          class="mt-2 text-xs font-medium text-secondary-500 hover:text-theme-text focus:outline-none disabled:opacity-50 flex items-center gap-1"
        >
          <i v-if="loadingMoreCities" class="pi pi-spin pi-spinner text-[10px]"></i>
          <i v-else class="pi pi-chevron-down text-[10px]"></i>
          {{ loadingMoreCities ? 'Loading...' : `Show More (${citiesRemaining})` }}
        </button>

        <!-- Show Less Cities -->
        <button
          v-if="citiesExpanded && !citiesHasMore"
          @click="store.collapseCities()"
          class="mt-2 text-xs font-medium text-secondary-500 hover:text-theme-text focus:outline-none flex items-center gap-1"
        >
          <i class="pi pi-chevron-up text-[10px]"></i>
          Show Less
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
/* Skeleton shimmer animation */
.skeleton-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shimmer 1.5s infinite;
}
@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* Premium scrollbar for filter lists */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: var(--color-secondary-500, #ffc107) transparent;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: var(--color-bgSecondary, #f5f5f5);
  border-radius: 8px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: var(--color-secondary-500, #ffc107);
  border-radius: 8px;
}
</style>
